import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

const FRAMEWORKS_API = '/v1/frameworks';

export interface FrameworkVersion {
    clauses: Array<string | { __ref: string }>;
    controls: Array<string | { __ref: string }>;
    tasks: Array<string | { __ref: string }>;
    version: string;
    designators?: string[];
    rootKey?: string;
    components?: Array<{ __ref: string }>;
}

export interface Framework {
    id?: string;
    aliases: string[];
    displayName: string;
    framework: {
        name: string;
    };
    patterns: string[];
    slug: string;
    version: FrameworkVersion;
}

/**
 * Gets a framework by its ID via Thalassa API
 */
export async function getFramework(frameworkId: string): Promise<Framework | null> {
    try {
        const res = await deiterateAPI.GET(`${FRAMEWORKS_API}/${frameworkId}`, {
            mode: 'cors',
            backend: 'thalassa'
        });
        return res.framework || null;
    } catch (error) {
        console.error('Error fetching framework:', error);
        return null;
    }
}

/**
 * Copies framework controls to a customer's SOA via Thalassa API
 */
export async function copyControlsToCustomerSOA(
    customerId: string,
    frameworkId: string,
    matchParams?: { [key: string]: string }
): Promise<void> {
    try {
        console.log(`Copying controls to customer SOA for framework ${frameworkId}`);
        
        const res = await deiterateAPI.POST(`${FRAMEWORKS_API}/${frameworkId}/copy-controls`, {
            mode: 'cors',
            backend: 'thalassa',
            headers: { 'X-Tenancy-Id': customerId, 'Company-ID': customerId },
            body: JSON.stringify({ matchParams })
        });

        console.log('Controls copied successfully:', res);
    } catch (error) {
        console.error('Error copying controls to customer SOA:', error);
        throw error;
    }
}

/**
 * Copies framework tasks to a customer's assurance tasks via Thalassa API
 */
export async function copyTasksToCustomerAssurance(
    customerId: string,
    frameworkId: string,
    matchParams?: { [key: string]: string },
    arrayInclusionFilter?: { path: string; value: any } | string
): Promise<void> {
    try {
        console.log(`Copying tasks to customer assurance for framework ${frameworkId}`);
        
        const res = await deiterateAPI.POST(`${FRAMEWORKS_API}/${frameworkId}/copy-tasks`, {
            mode: 'cors',
            backend: 'thalassa',
            headers: { 'X-Tenancy-Id': customerId, 'Company-ID': customerId },
            body: JSON.stringify({ matchParams, arrayInclusionFilter })
        });

        console.log('Tasks copied successfully:', res);
    } catch (error) {
        console.error('Error copying tasks to customer assurance:', error);
        throw error;
    }
}

/**
 * Copies all framework components (controls and tasks) to a customer's collections
 */
export async function copyFrameworkToCustomer(
    customerId: string,
    frameworkId: string,
    matchParams?: { [key: string]: string }
): Promise<{ controlsCount: number; tasksCount: number }> {
    await copyControlsToCustomerSOA(customerId, frameworkId, matchParams);
    await copyTasksToCustomerAssurance(customerId, frameworkId, matchParams);

    const framework = await getFramework(frameworkId);
    const controlsCount = framework?.version?.controls?.length || 0;
    const tasksCount = framework?.version?.tasks?.length || 0;

    return { controlsCount, tasksCount };
}

/**
 * Finds a framework by its name or alias via Thalassa API
 */
export async function findFrameworkByName(name: string): Promise<Framework | null> {
    try {
        const res = await deiterateAPI.GET(`${FRAMEWORKS_API}/search/${encodeURIComponent(name)}`, {
            mode: 'cors',
            backend: 'thalassa'
        });
        return res.framework || null;
    } catch (error) {
        console.error('Error searching framework:', error);
        return null;
    }
}

/**
 * Lists all frameworks via Thalassa API
 */
export async function listFrameworks(): Promise<Framework[]> {
    try {
        const res = await deiterateAPI.GET(FRAMEWORKS_API, {
            mode: 'cors',
            backend: 'thalassa'
        });
        return res.frameworks || [];
    } catch (error) {
        console.error('Error listing frameworks:', error);
        return [];
    }
}